import React, { useState, useEffect, useCallback } from 'react';

const MOBILE_BREAKPOINT = 768;

interface ResponsivePanelContainerProps {
    children: React.ReactNode;
    width?: string;
    position?: 'left' | 'right';
}

const ResponsivePanelContainer: React.FC<ResponsivePanelContainerProps> = ({
    children,
    width = '350px',
    position = 'left',
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
    const [isOpen, setIsOpen] = useState(false);

    const handleResize = useCallback(() => {
        const mobile = window.innerWidth < MOBILE_BREAKPOINT;
        setIsMobile(mobile);
        if (!mobile) setIsOpen(false);
    }, []);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    if (!isMobile) {
        const style: React.CSSProperties = {
            position: 'absolute',
            top: '1rem',
            bottom: '1rem',
            width,
            zIndex: 10,
            transition: 'all 0.5s ease-in-out',
        };
        if (position === 'left') {
            style.left = '1rem';
        } else {
            style.right = '1rem';
        }
        return <div style={style}>{children}</div>;
    }

    // Mobile: toggle button + sliding panel
    const isLeft = position === 'left';

    const panelStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '85vw',
        maxWidth: '350px',
        zIndex: 20,
        transition: 'transform 0.3s ease-in-out',
        ...(isLeft
            ? { left: 0, transform: isOpen ? 'translateX(0)' : 'translateX(-100%)' }
            : { right: 0, transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }
        ),
    };

    const toggleStyle: React.CSSProperties = {
        position: 'absolute',
        top: '3.5rem',
        zIndex: 15,
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        color: '#93c5fd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '18px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        ...(isLeft ? { left: '0.75rem' } : { right: '0.75rem' }),
    };

    const backdropStyle: React.CSSProperties = {
        position: 'absolute',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        zIndex: 15,
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: 'opacity 0.3s ease-in-out',
    };

    return (
        <>
            {!isOpen && (
                <button style={toggleStyle} onClick={() => setIsOpen(true)} aria-label="Open settings">
                    &#9881;
                </button>
            )}
            <div style={backdropStyle} onClick={() => setIsOpen(false)} />
            <div style={panelStyle}>{children}</div>
        </>
    );
};

export default ResponsivePanelContainer;
